import { useState, useEffect } from 'react'
import { X } from 'lucide-react'
import { getPopup } from '@/sanity/queries'

interface PopupData {
  attivo: boolean
  etichetta?: string
  titolo?: string
  sottotitolo?: string
  immagine?: string
  linkHref?: string
  linkLabel?: string
}

const SESSION_KEY = 'popup-agv-v1'

interface AlertPopupProps {
  /** Se true, forza l'apertura (es. dal tasto in navbar) ignorando sessionStorage */
  forceOpen?: boolean
  /** Callback chiamato quando il popup viene chiuso */
  onClose?: () => void
}

export default function AlertPopup({ forceOpen = false, onClose }: AlertPopupProps) {
  const [visible, setVisible] = useState(false)
  const [data, setData] = useState<PopupData | null>(null)

  // Carica i dati da Sanity una volta sola al mount
  useEffect(() => {
    getPopup().then((popup: PopupData | null) => {
      if (!popup) return
      setData(popup)
      // Apertura automatica: solo se attivo e non ancora visto in questa sessione
      if (popup.attivo && !sessionStorage.getItem(SESSION_KEY)) {
        setVisible(true)
      }
    }).catch(() => {})
  }, [])

  // Quando il tasto navbar attiva forceOpen, apri sempre (anche se già visto)
  useEffect(() => {
    if (forceOpen) setVisible(true)
  }, [forceOpen])

  const chiudi = () => {
    // Salva in sessionStorage solo per le aperture automatiche
    if (!forceOpen) sessionStorage.setItem(SESSION_KEY, 'true')
    setVisible(false)
    onClose?.()
  }

  if (!visible || !data) return null

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80">
      <div
        className="relative flex flex-col rounded-lg overflow-hidden"
        style={{
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(212,175,55,0.35)',
          maxHeight: '92vh',
          maxWidth: '420px',
          width: '100%',
        }}
      >
        {/* Header */}
        <div
          className="shrink-0 flex items-center justify-between px-4 py-3"
          style={{ borderBottom: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(212,175,55,0.08)' }}
        >
          <div className="flex items-center gap-2">
            <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', backgroundColor: '#d4af37' }} />
            <span className="font-sans text-xs uppercase tracking-[0.15em] font-semibold" style={{ color: '#d4af37' }}>
              {data.etichetta || 'Avviso'}
            </span>
          </div>
          <button
            onClick={chiudi}
            className="transition-colors p-1"
            style={{ color: 'rgba(255,255,255,0.45)' }}
            onMouseOver={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.9)')}
            onMouseOut={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.45)')}
            aria-label="Chiudi"
          >
            <X size={17} />
          </button>
        </div>

        {/* Foto (se presente) */}
        {data.immagine && (
          <div className="overflow-y-auto shrink-1 min-h-0">
            <img
              src={data.immagine}
              alt={data.titolo || ''}
              className="w-full block"
              style={{ objectFit: 'contain' }}
            />
          </div>
        )}

        {/* Info evento + pulsante */}
        <div
          className="shrink-0 px-4 py-3 flex items-center justify-between gap-3"
          style={{ borderTop: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.4)' }}
        >
          <div className="min-w-0">
            {data.titolo && (
              <p className="font-serif text-sm font-semibold leading-snug" style={{ color: '#f0e6c0' }}>
                {data.titolo}
              </p>
            )}
            {data.sottotitolo && (
              <p className="font-sans text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                {data.sottotitolo}
              </p>
            )}
          </div>

          {data.linkHref ? (
            <a
              href={data.linkHref}
              onClick={chiudi}
              className="shrink-0 px-4 py-1.5 font-sans text-xs font-medium rounded-sm transition-colors duration-200 tracking-wide"
              style={{ backgroundColor: 'rgba(212,175,55,0.15)', color: '#d4af37', border: '1px solid rgba(212,175,55,0.4)', textDecoration: 'none' }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.28)')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.15)')}
            >
              {data.linkLabel || 'Scopri di più'}
            </a>
          ) : (
            <button
              onClick={chiudi}
              className="shrink-0 px-4 py-1.5 font-sans text-xs font-medium rounded-sm transition-colors duration-200 tracking-wide"
              style={{ backgroundColor: 'rgba(212,175,55,0.15)', color: '#d4af37', border: '1px solid rgba(212,175,55,0.4)' }}
              onMouseOver={e => (e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.28)')}
              onMouseOut={e => (e.currentTarget.style.backgroundColor = 'rgba(212,175,55,0.15)')}
            >
              Chiudi
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
